// SubRailLayout — Pattern C secondary rail (LAYOUT_SYSTEM.md §1.2).
// Desktop: [ sub-rail 220px | <Outlet/> ]. Mobile: the sub-rail collapses
// to a horizontal scrollable tab strip above the content (no nested
// drawer — keeps phone usability). Used by Trends and Settings.

import { NavLink, Outlet } from "react-router-dom";

export type SubRailItem = {
  to: string;
  label: string;
  /** Pass for the index tab so it doesn't stay active on child routes. */
  end?: boolean;
};

export function SubRailLayout({
  title,
  items,
}: {
  title: string;
  items: SubRailItem[];
}) {
  return (
    <div className="md:flex md:gap-8 -mx-4 md:mx-0 -my-6 md:my-0">
      {/* Desktop secondary rail */}
      <aside className="hidden md:block w-[220px] shrink-0 border-r border-ink-line pr-4 py-2">
        <div className="rail-title px-2 pb-3">{title}</div>
        <nav aria-label={`${title} sections`}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rail-link ${isActive ? "rail-link-active" : ""}`
              }
            >
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile tab strip */}
      <div className="md:hidden sticky top-[57px] z-20 bg-canvas/90 backdrop-blur border-b border-ink-line px-4">
        <div className="flex gap-1 overflow-x-auto py-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition duration-fast ${
                  isActive
                    ? "bg-accent/10 text-accent"
                    : "text-ink-muted hover:text-ink"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 px-4 md:px-0 py-6 md:py-2">
        <Outlet />
      </div>
    </div>
  );
}
